import React, { useState, useEffect } from 'react';
import { Users, Calendar, ChefHat, Cake, Star, Gift, Crown, Diamond, Send, MapPin } from 'lucide-react';
import { mockData } from '../mock';
import { formHandlers, trackPageView } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { PaymentSelection } from '../components/PaymentSelection';
import { useToast } from '../hooks/use-toast';

export const Inquiry = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    city: '',
    state: '',
    guestCount: '',
    specialtyInterests: ''
  });
  
  const [cakeFormData, setCakeFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    cakeType: '',
    servingSize: '',
    designPreferences: '',
    flavorPreferences: '',
    budgetRange: '',
    specialRequests: ''
  });
  
  const [activeForm, setActiveForm] = useState('event'); // 'event' or 'cake'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showPaymentSelection, setShowPaymentSelection] = useState(false);
  const { toast } = useToast();

  // Track page view on component mount
  useEffect(() => {
    trackPageView('/inquiry');
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCakeInputChange = (e) => {
    const { name, value } = e.target;
    setCakeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await formHandlers.submitEventInquiry(formData);
      if (result.success) {
        toast({
          title: "Event Inquiry Submitted!",
          description: result.message,
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventDate: '',
          city: '',
          state: '',
          guestCount: '',
          specialtyInterests: ''
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCakeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await formHandlers.submitCakeInquiry(cakeFormData);
      if (result.success) {
        toast({
          title: "Cake Inquiry Submitted!",
          description: "We'll contact you within 24 hours to discuss your custom cake design!",
        });
        // Reset cake form
        setCakeFormData({
          name: '',
          email: '',
          phone: '',
          eventDate: '',
          cakeType: '',
          servingSize: '',
          designPreferences: '',
          flavorPreferences: '',
          budgetRange: '',
          specialRequests: ''
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTierSubscription = async (tier) => {
    setSelectedTier(tier);
    setShowPaymentSelection(true);
  };

  const handlePaymentCompleted = async (paymentData) => {
    try {
      const subscriptionData = {
        tier_name: selectedTier.name,
        email: paymentData.email || formData.email || cakeFormData.email,
        customer_info: {
          name: paymentData.name || formData.name || cakeFormData.name,
          phone: paymentData.phone || formData.phone || cakeFormData.phone
        },
        payment_method: paymentData.method
      };

      const result = await formHandlers.subscribeToTier(subscriptionData);
      
      if (result.success) {
        toast({
          title: "Subscription Activated!",
          description: result.message,
        });
      } else {
        toast({
          title: "Subscription Error",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate subscription. Please try again.",
        variant: "destructive",
      });
    }
    
    setShowPaymentSelection(false);
    setSelectedTier(null);
  };

  return (
    <div>
      {/* Inquiry Hero */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>Luxury Hospitality Inquiry</h1>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '700px', 
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              From intimate gatherings to grand celebrations, weddings to corporate events, travel groups to exclusive retreats — 
              let us provide the unforgettable moments and services that reach unparalleled sophistication.
            </p>
          </div>
        </div>
      </section>

      {/* Inquiry Forms */}
      <section style={{ background: 'var(--bg-secondary)', padding: '80px 0' }}>
        <div className="container">
          {/* Form Toggle */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{ 
              display: 'inline-flex',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              border: '1px solid var(--border-light)',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setActiveForm('event')}
                style={{
                  padding: '16px 32px',
                  background: activeForm === 'event' ? 'var(--color-fence-green)' : 'transparent',
                  color: activeForm === 'event' ? 'white' : 'var(--text-primary)',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Event Planning
              </button>
              <button
                onClick={() => setActiveForm('cake')}
                style={{
                  padding: '16px 32px',
                  background: activeForm === 'cake' ? 'var(--color-gold)' : 'transparent',
                  color: activeForm === 'cake' ? 'white' : 'var(--text-primary)',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Custom Cake Design
              </button>
            </div>
          </div>

          <div className="grid-two-column" style={{ alignItems: 'flex-start' }}>
            {/* Event Planning Form */}
            {activeForm === 'event' && (
              <Card style={{
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                background: 'var(--bg-primary)',
                padding: '40px'
              }}>
                <CardHeader>
                  <CardTitle className="heading-1">Event Inquiry Form</CardTitle>
                  <CardDescription className="body-regular">
                    Please provide details about your event so we can create the perfect culinary experience for you.
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Name */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        style={{ borderRadius: '0px' }}
                        placeholder="Enter your full name"
                      />
                    </div>

                    {/* Email & Phone */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label className="body-regular" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          style={{ borderRadius: '0px' }}
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="body-regular" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          Phone Number *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          style={{ borderRadius: '0px' }}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    {/* Event Date */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Event Date *
                      </label>
                      <Input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleInputChange}
                        required
                        style={{ borderRadius: '0px' }}
                      />
                    </div>

                    {/* City & State */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label className="body-regular" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          City *
                        </label>
                        <Input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          style={{ borderRadius: '0px' }}
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <label className="body-regular" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          State *
                        </label>
                        <Input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          required
                          style={{ borderRadius: '0px' }}
                          placeholder="State"
                        />
                      </div>
                    </div>

                    {/* Guest Count */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Expected Number of Guests *
                      </label>
                      <Input
                        type="number"
                        name="guestCount"
                        value={formData.guestCount}
                        onChange={handleInputChange}
                        required
                        min="1"
                        style={{ borderRadius: '0px' }}
                        placeholder="e.g., 25"
                      />
                    </div>

                    {/* Specialty Food Interests */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Specialty Food Interests & Dietary Requirements
                      </label>
                      <Textarea
                        name="specialtyInterests"
                        value={formData.specialtyInterests}
                        onChange={handleInputChange}
                        rows={4}
                        style={{ borderRadius: '0px' }}
                        placeholder="Tell us about your food preferences, dietary restrictions, cuisine interests, or any special requests..."
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="btn-primary"
                      disabled={isSubmitting}
                      style={{ 
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Send size={16} />
                      {isSubmitting ? 'Submitting Event Inquiry...' : 'Submit Event Inquiry'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Custom Cake Form */}
            {activeForm === 'cake' && (
              <Card style={{
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                background: 'var(--bg-primary)',
                padding: '40px'
              }}>
                <CardHeader>
                  <CardTitle className="heading-1">Custom Cake Design</CardTitle>
                  <CardDescription className="body-regular">
                    Tell us about your dream cake! We'll create a custom design that perfectly matches your vision and celebration.
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <form onSubmit={handleCakeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Name */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={cakeFormData.name}
                        onChange={handleCakeInputChange}
                        required
                        style={{ borderRadius: '0px' }}
                        placeholder="Enter your full name"
                      />
                    </div>

                    {/* Email & Phone */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label className="body-regular" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={cakeFormData.email}
                          onChange={handleCakeInputChange}
                          required
                          style={{ borderRadius: '0px' }}
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="body-regular" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          Phone Number *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={cakeFormData.phone}
                          onChange={handleCakeInputChange}
                          required
                          style={{ borderRadius: '0px' }}
                          placeholder="(973) 666-3705"
                        />
                      </div>
                    </div>

                    {/* Event Date */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Event Date *
                      </label>
                      <Input
                        type="date"
                        name="eventDate"
                        value={cakeFormData.eventDate}
                        onChange={handleCakeInputChange}
                        required
                        style={{ borderRadius: '0px' }}
                      />
                    </div>

                    {/* Cake Type & Serving Size */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label className="body-regular" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          Occasion/Cake Type *
                        </label>
                        <Input
                          type="text"
                          name="cakeType"
                          value={cakeFormData.cakeType}
                          onChange={handleCakeInputChange}
                          required
                          style={{ borderRadius: '0px' }}
                          placeholder="Wedding, Birthday, Baby Shower, etc."
                        />
                      </div>
                      <div>
                        <label className="body-regular" style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          Serving Size *
                        </label>
                        <Input
                          type="text"
                          name="servingSize"
                          value={cakeFormData.servingSize}
                          onChange={handleCakeInputChange}
                          required
                          style={{ borderRadius: '0px' }}
                          placeholder="e.g., 50 people, 2-tier, 10 inch"
                        />
                      </div>
                    </div>

                    {/* Budget Range */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Budget Range
                      </label>
                      <Input
                        type="text"
                        name="budgetRange"
                        value={cakeFormData.budgetRange}
                        onChange={handleCakeInputChange}
                        style={{ borderRadius: '0px' }}
                        placeholder="e.g., $200-$500, $500-$1000, $1000+"
                      />
                    </div>

                    {/* Design Preferences */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Design Preferences
                      </label>
                      <Textarea
                        name="designPreferences"
                        value={cakeFormData.designPreferences}
                        onChange={handleCakeInputChange}
                        rows={3}
                        style={{ borderRadius: '0px' }}
                        placeholder="Describe your vision: colors, theme, style, decorations, specific elements you'd like included..."
                      />
                    </div>

                    {/* Flavor Preferences */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Flavor Preferences
                      </label>
                      <Textarea
                        name="flavorPreferences"
                        value={cakeFormData.flavorPreferences}
                        onChange={handleCakeInputChange}
                        rows={2}
                        style={{ borderRadius: '0px' }}
                        placeholder="Preferred cake flavors, fillings, frostings... (Vanilla, Chocolate, Red Velvet, Lemon, etc.)"
                      />
                    </div>

                    {/* Special Requests */}
                    <div>
                      <label className="body-regular" style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        color: 'var(--text-primary)'
                      }}>
                        Special Requests
                      </label>
                      <Textarea
                        name="specialRequests"
                        value={cakeFormData.specialRequests}
                        onChange={handleCakeInputChange}
                        rows={2}
                        style={{ borderRadius: '0px' }}
                        placeholder="Any allergies, dietary restrictions, delivery requirements, or other special considerations..."
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="btn-gold"
                      disabled={isSubmitting}
                      style={{ 
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Send size={16} />
                      {isSubmitting ? 'Submitting Cake Inquiry...' : 'Submit Cake Design Request'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Info Sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              <Card style={{
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                background: 'var(--color-lavender)',
                padding: '32px'
              }}>
                <CardHeader>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'var(--color-fence-green)',
                    borderRadius: '0px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <Calendar size={24} color="white" />
                  </div>
                  <CardTitle className="heading-3">Planning Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    We recommend booking at least 2-4 weeks in advance for optimal planning and menu customization. 
                    Last-minute requests welcome based on availability.
                  </p>
                </CardContent>
              </Card>

              <Card style={{
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                background: 'var(--bg-primary)',
                padding: '32px'
              }}>
                <CardHeader>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'var(--color-gold)',
                    borderRadius: '0px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <Users size={24} color="white" />
                  </div>
                  <CardTitle className="heading-3">Group Sizes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    From intimate dinners for 2 to grand celebrations for 200+. 
                    Every event size receives our signature attention to detail and culinary excellence.
                  </p>
                </CardContent>
              </Card>

              <Card style={{
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                background: 'var(--bg-primary)',
                padding: '32px'
              }}>
                <CardHeader>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'var(--color-fence-green)',
                    borderRadius: '0px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <MapPin size={24} color="white" />
                  </div>
                  <CardTitle className="heading-3">Service Areas</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    We provide services throughout the region. Travel fees may apply for locations outside our standard service area. 
                    Contact us for specific availability.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Cake Portfolio */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>Our Custom Cake Portfolio</h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '700px', 
              margin: '0 auto' 
            }}>
              Each cake is a unique work of art, crafted with precision and passion. From elegant wedding cakes 
              to whimsical celebration designs, we bring your sweetest dreams to life.
            </p>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '32px', 
            marginBottom: '60px' 
          }}>
            {mockData.customCakes.slice(0, 6).map((cake) => (
              <div key={cake.id} className="hover-lift" style={{ cursor: 'pointer' }}>
                <img
                  src={cake.url}
                  alt={cake.alt}
                  style={{
                    width: '100%',
                    height: '280px',
                    objectFit: 'cover',
                    marginBottom: '16px',
                    borderRadius: '0px'
                  }}
                />
                <div style={{ 
                  background: 'var(--bg-primary)', 
                  padding: '20px',
                  border: '1px solid var(--border-light)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h3 className="heading-3" style={{ fontSize: '16px' }}>{cake.title}</h3>
                    <span style={{
                      background: 'var(--color-gold)',
                      color: 'white',
                      padding: '4px 8px',
                      fontSize: '10px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {cake.category}
                    </span>
                  </div>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {cake.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <Button className="btn-secondary">
              <a href="/custom-cakes" style={{ textDecoration: 'none', color: 'inherit' }}>
                View Complete Cake Portfolio
              </a>
            </Button>
          </div>
          
          <div style={{
            textAlign: 'center',
            background: 'var(--color-lavender)',
            padding: '60px 40px',
            borderRadius: '0px'
          }}>
            <h3 className="heading-1" style={{ marginBottom: '24px' }}>Ready to Design Your Dream Cake?</h3>
            <p className="body-large" style={{ 
              marginBottom: '32px', 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto 32px auto'
            }}>
              Every cake tells a story. Let us help you tell yours with a custom design that's as unique 
              and special as your celebration.
            </p>
            <Button 
              className="btn-gold"
              onClick={() => setActiveForm('cake')}
            >
              Start Your Custom Cake Design
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding-small">
        <div className="container">
          <div style={{ 
            textAlign: 'center',
            background: 'var(--bg-secondary)',
            padding: '60px 40px',
            borderRadius: '0px'
          }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>Need to Speak With Us?</h2>
            <p className="body-large" style={{ 
              marginBottom: '32px', 
              color: 'var(--text-secondary)',
              maxWidth: '500px',
              margin: '0 auto 32px auto'
            }}>
              For urgent inquiries or detailed consultations, feel free to reach out directly. 
              We're here to make your culinary vision a reality.
            </p>
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button className="btn-secondary">
                Call Us: (973) 666-3705
              </Button>
              <Button className="btn-secondary">
                Email: info@decadentpalete.com
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Program Highlight */}
      <section style={{ background: 'var(--color-fence-green)', padding: '80px 0', color: 'white' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '24px', color: 'white' }}>
              DP Referral Program
            </h2>
            <p className="body-large" style={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              maxWidth: '700px', 
              margin: '0 auto' 
            }}>
              Share the exceptional Decadent Palete experience with friends and earn rewards! 
              Our exclusive referral program offers generous commissions for every successful membership referral.
            </p>
          </div>
          
          <div className="grid-three-column">
            {mockData.subscriptionTiers.map((tier, index) => (
              <div key={tier.id} style={{
                background: 'rgba(255, 255, 255, 0.1)',
                padding: '40px 32px',
                borderRadius: '0px',
                textAlign: 'center',
                border: tier.isRoyal ? '2px solid var(--color-gold)' : '1px solid rgba(255, 255, 255, 0.2)',
                position: 'relative'
              }}>
                {tier.isRoyal && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--color-gold)',
                    color: 'black',
                    padding: '8px 24px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    ROYAL TIER
                  </div>
                )}
                
                <h3 className="heading-2" style={{ marginBottom: '16px', color: 'white' }}>
                  {tier.name}
                </h3>
                
                <div style={{ 
                  marginBottom: '24px',
                  padding: '24px',
                  background: 'var(--color-gold)',
                  color: 'black',
                  borderRadius: '0px'
                }}>
                  <div className="hero-large" style={{ color: 'black', marginBottom: '8px' }}>
                    {tier.referralRate}%
                  </div>
                  <p className="body-regular" style={{ color: 'black', margin: 0, fontWeight: '600' }}>
                    Commission Rate
                  </p>
                </div>
                
                <p className="body-regular" style={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: '24px' 
                }}>
                  Earn <strong style={{ color: 'var(--color-gold)' }}>{tier.referralRate}%</strong> commission 
                  for every friend who joins the DP exclusive membership through your referral.
                </p>
                
                <p className="body-small" style={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontStyle: 'italic'
                }}>
                  Monthly membership: <span style={{ color: 'var(--color-gold)' }}>Contact for Quote</span>
                </p>
              </div>
            ))}
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '60px' }}>
            <p className="body-large" style={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              marginBottom: '32px',
              maxWidth: '600px',
              margin: '0 auto 32px auto'
            }}>
              Start earning today! Share your unique referral code with friends and family. 
              Commissions are paid monthly and can be applied to your membership or withdrawn.
            </p>
            
            <Button 
              className="btn-gold"
              style={{ marginRight: '16px' }}
            >
              Get My Referral Code
            </Button>
            <Button 
              style={{
                background: 'transparent',
                color: 'white',
                border: '1px solid white',
                borderRadius: '0px',
                padding: '19px 23px',
                minWidth: '210px',
                height: '60px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Subscription Tiers */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '24px' }}>Subscription Rewards</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Join our exclusive membership program and enjoy exceptional benefits, loyalty rewards, and early access 
              to our hidden menu featuring Caribbean, French, and Indian influence fusion cuisine.
            </p>
          </div>
          
          <div className="grid-three-column">
            {mockData.subscriptionTiers.map((tier, index) => (
              <Card key={tier.id} className="hover-lift" style={{
                border: tier.isRoyal ? '2px solid var(--color-gold)' : '1px solid var(--border-light)',
                borderRadius: '0px',
                background: tier.isRoyal ? 'var(--color-lavender)' : 'var(--bg-primary)',
                padding: '40px 32px',
                textAlign: 'center',
                position: 'relative'
              }}>
                {tier.isRoyal && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--color-gold)',
                    color: 'white',
                    padding: '8px 24px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    ROYAL TIER
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="heading-2" style={{ marginBottom: '16px' }}>
                    {tier.name}
                  </CardTitle>
                  <div style={{ marginBottom: '24px' }}>
                    <span className="hero-medium" style={{ color: 'var(--color-gold)' }}>
                      Contact for Quote
                    </span>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0, marginBottom: '32px' }}>
                    {tier.benefits.map((benefit, idx) => {
                      const isReferralBenefit = benefit.includes('Referral Program');
                      return (
                        <li key={idx} className="body-small" style={{ 
                          marginBottom: '12px',
                          color: isReferralBenefit ? 'var(--color-gold)' : 'var(--text-primary)',
                          paddingLeft: '20px',
                          position: 'relative',
                          fontWeight: isReferralBenefit ? '600' : 'normal'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '6px',
                            height: '6px',
                            background: isReferralBenefit ? 'var(--color-gold)' : (tier.isRoyal ? 'var(--color-gold)' : 'var(--color-fence-green)'),
                            borderRadius: '50%'
                          }}></span>
                          {benefit}
                        </li>
                      );
                    })}
                  </ul>
                  
                  <p className="body-small" style={{ 
                    color: 'var(--text-secondary)', 
                    fontStyle: 'italic',
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}>
                    {tier.note}
                  </p>
                </CardContent>
                
                <CardFooter>
                  <Button 
                    className={tier.isRoyal ? "btn-gold" : "btn-primary"}
                    onClick={() => handleTierSubscription(tier)}
                    disabled={isSubmitting}
                    style={{ width: '100%' }}
                  >
                    {isSubmitting ? 'Processing...' : 'Request Quote'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          {/* Payment Selection Modal */}
          {showPaymentSelection && selectedTier && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}>
              <div style={{ 
                maxWidth: '700px', 
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                <PaymentSelection
                  amount={0}
                  orderType="subscription"
                  orderDetails={{
                    tierName: selectedTier.name,
                    features: selectedTier.benefits
                  }}
                  onPaymentCompleted={handlePaymentCompleted}
                  onClose={() => {
                    setShowPaymentSelection(false);
                    setSelectedTier(null);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};